import React, {
  FC,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  Box,
  Checkbox,
  List,
  PopoverOrigin,
  Skeleton,
  Typography,
} from '@mui/material';
import {
  ConfirmationTypes,
  ITEMS_PER_PAGE,
  deleteAllNotification,
  deleteSelectedNotification,
  getNotificationsList,
  getNotificationsListPagination,
  markAsRead,
  markAsReadSelected,
  notificationActions,
  notificationsList,
  successOrSelector,
} from '@pharmaplan/common';

import { INotificationsData } from '@pharmaplan/common/models/NotificationsModel';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { resetDialog, setDialog } from '../../../reducers/dialogReducer';
import { useAppSelector } from '../../../hooks/useAppSelector';
import ConfirmationModal from '../../Modals/ConfirmationModal';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import CustomButton from '../../common/CustomButton';
import strings from '../../../localization';

import NotificationItem from './NotificationItems/NotificationItem';
import classes from './styles';
import useNotificationDelete from '../../../hooks/useNotificationDelete';
import CustomMenuList from '../../common/CustomMenuList';

interface INotifications {
  closeNotification: () => void;
}

const anchor = { horizontal: 'center', vertical: 'center' };

const skeletonConfig = [
  {
    key: 0,
    width: '80%',
  },
  {
    key: 1,
    width: '60%',
  },
  {
    key: 2,
    width: '40%',
  },
];

const Notifications: FC<INotifications> = ({ closeNotification }) => {
  const [notifData, setNotifData] = useState<Array<INotificationsData>>([]);
  const listEndRef = useRef(null);
  const [page, setPage] = useState(1);

  const dispatch = useAppDispatch();
  const { selected, setSelected, clearSelection } = useNotificationDelete();

  const allNotifications = useAppSelector(notificationsList);
  const loadSuccess = useAppSelector((state) =>
    successOrSelector([notificationActions.getNotificationsList], state));

  const noData = !notifData.length;
  const { data } = allNotifications ?? {};

  const { selectedId } = selected;
  const NoItemSelected = selectedId.length === 0;

  const observerCallback = useCallback(
    (entries: { isIntersecting: any }[]) => {
      const paginationCondition = notifData.length >= page * ITEMS_PER_PAGE;

      if (paginationCondition && entries[0].isIntersecting) {
        setPage((prevPage) =>
          prevPage + 1);
      }
    },
    [page, notifData],
  );

  const handleNotifDelete = (notificationId: string) => {
    const remainingNotifs = notifData.filter(
      (item) =>
        item.notificationId !== notificationId,
    );
    setNotifData(remainingNotifs);
  };

  const handleMarkAsRead = (id: string) => {
    setNotifData((prev) =>
      prev.map((item) => {
        if (item.notificationId === id) {
          return { ...item, isVisited: true };
        }

        return item;
      }));
  };

  const handleMarkAllAsRead = (
    e?: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    e?.stopPropagation();
    dispatch(markAsRead());
    setNotifData((prev) =>
      prev.map((item) =>
        ({
          ...item,
          isVisited: true,
        })));
  };

  const handleSelectedAsRead = () => {
    dispatch(markAsReadSelected(selectedId));
    setNotifData((prev) =>
      prev.map((item) =>
        ({
          ...item,
          isVisited: selectedId.includes(item.notificationId)
            ? true
            : item.isVisited,
        })));
    clearSelection();
  };

  const deleteSelected = () => {
    dispatch(deleteSelectedNotification(selectedId));
    setNotifData((prev) =>
      prev.filter((item) =>
        !selectedId.includes(item.notificationId)));
    clearSelection();
  };

  const deleteAll = (e?: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e?.stopPropagation();
    dispatch(deleteAllNotification());
    setNotifData([]);
    dispatch(resetDialog());
  };

  const deleteConfirmation = (
    e?: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    dispatch(
      setDialog({
        Component: (
          <ConfirmationModal
            type={ConfirmationTypes.clearAll}
            confirmAction={() =>
              deleteAll(e)}
          />
        ),
        heading: { title: '' },
        showCloseButton: false,
      }),
    );
    e?.stopPropagation();
  };

  const ListItems = useCallback(
    () =>
      (noData ? (
        <Box sx={classes.emptyContainer}>
          <Typography>{strings.noDataAvailable}</Typography>
        </Box>
      ) : (
        <>
          {notifData.map((item: INotificationsData) => {
            const { notificationId } = item ?? {};

            return (
              <NotificationItem
                key={notificationId}
                selected={selected}
                item={item}
                setSelected={setSelected}
                closeNotification={closeNotification}
                handleDeleteNotif={handleNotifDelete}
                handleMarkAsRead={handleMarkAsRead}
              />
            );
          })}
        </>
      )),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [noData, notifData, selected],
  );

  useEffect(() => {
    setNotifData(data);
  }, [data]);

  useEffect(() => {
    dispatch(getNotificationsList());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    page !== 1
      && dispatch(
        getNotificationsListPagination({ page, itemPerPage: ITEMS_PER_PAGE }),
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  useEffect(() => {
    const observer = new IntersectionObserver(observerCallback, {
      root: null,
      rootMargin: '0px',
      threshold: 1.0,
    });

    if (listEndRef.current) {
      observer.observe(listEndRef.current);
    }

    return () => {
      if (listEndRef.current) {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        observer.unobserve(listEndRef.current);
      }
    };
  }, [observerCallback]);

  const menuItems = () =>
    [
      {
        key: '1',
        label: strings.formatString(
          strings.markSelectedAsRead,
          `${selectedId.length}`,
        ),
        onClick: handleSelectedAsRead,
      },
      {
        key: '3',
        label: strings.formatString(
          strings.deleteSelectedCount,
          `${selectedId.length}`,
        ),
        onClick: deleteSelected,
      },
    ];

  const handleOnChange = () => {
    const ids = notifData.map((item) => {
      const { notificationId } = item ?? {};
      return notificationId;
    });

    if (selected.isSelected) {
      setSelected((prev) =>
        ({
          isSelected: !prev.isSelected,
          selectedId: [],
        }));
    } else {
      setSelected((prev) =>
        ({
          isSelected: !prev.isSelected,
          selectedId: ids,
        }));
    }
  };

  return (
    <Box sx={classes.root}>
      <Box sx={classes.notificationHeader}>
        <Box sx={classes.headerItems}>
          <Checkbox edge="start" onChange={handleOnChange} />
          <Typography sx={classes.notificationTitle}>
            {strings.notifications}
          </Typography>
        </Box>
        <Box sx={classes.notificationSubContainer}>
          <CustomButton
            variant="text"
            label={strings.markAsRead}
            customButtonStyle={classes.notificationButtons}
            customClass={classes.markReadButton}
            onClick={handleMarkAllAsRead}
          />
          <CustomButton
            variant="text"
            onClick={deleteConfirmation}
            label={strings.clearAll}
            customButtonStyle={classes.notificationButtons}
          />
          <CustomMenuList
            margin={60}
            items={menuItems()}
            anchorOrigin={anchor as PopoverOrigin}
            keepMounted
            disableButton={NoItemSelected}
          >
            <MoreVertIcon
              sx={NoItemSelected ? classes.disabledMenu : classes.enabledMenu}
            />
          </CustomMenuList>
        </Box>
      </Box>
      <List sx={classes.container}>
        {loadSuccess ? (
          <ListItems />
        ) : (
          <Box sx={classes.skeletonContainer}>
            <Skeleton variant="circular" sx={classes.skeletonAvatar} />

            <Box sx={classes.skeletonTextcontainer}>
              {skeletonConfig.map((item) => {
                const { key, width } = item ?? {};

                return (
                  <Skeleton
                    key={key}
                    variant="text"
                    sx={classes.skeletonText}
                    width={width}
                  />
                );
              })}
            </Box>
          </Box>
        )}
      </List>
      <Box component="div" ref={listEndRef} />
    </Box>
  );
};

export default memo(Notifications);
